import { useCallback, useMemo } from 'react';
import type { Moment } from 'moment';
import type { RangeValue } from 'rc-picker/lib/interface';
import {
    clampToMaxPeriod,
    handleDateParamsUpdate,
} from '~utils/date-picker-helpers';
import type { ApiPeriod } from '../kpis/periods';
import { useRegionProvinces } from '~components/analytics-search-options/region-provinces/use-region-provinces';
import type { ApplicationTargetEnum } from '~types/app-selection';
import { resolveCompareEnabled } from '../kpis/resolvers';
import type { ResolvedPeriod, ResolvedRegion } from '../kpis/types';
import {
    coerceQueryProvinces,
    PageCampaign,
    resolvePageFilter,
} from './page-filter';

/** The query params an analytics page's filter lives in. */
export type PageFilterQuery = {
    period?: string;
    startDate?: string;
    endDate?: string;
    provinces?: string | string[];
    compare?: boolean;
};

/** The slice of `useSearchParams` this module needs. */
export interface PageFilterSearch {
    params: PageFilterQuery;
    setParams: (params: Partial<PageFilterQuery>) => void;
}

/**
 * A page's region inputs, or `'none'` for a page with no region concept: no
 * picker, no province in its request. `'none'` locks `filter.provinces` to
 * `[]` and never touches a `provinces` param, regardless of what's passed.
 */
export type PageFilterRegion =
    | 'none'
    | {
          appTarget: ApplicationTargetEnum | undefined;
          defaultProvincesCsv?: string;
      };

/**
 * The page filter, resolved once: what the toolbar displays IS what the cards
 * fetch. `range` is clamped to the period cap, so a hand-edited URL cannot
 * outrun what the picker would have allowed.
 */
export interface ResolvedPageFilter {
    range: ResolvedPeriod;
    /** The bar's capsule the range is, absent for a hand-picked range. */
    preset?: ApiPeriod;
    provinces: ResolvedRegion;
    compare: boolean;
    /** A QC-target app has no region choice to offer: the selector is read-only. */
    regionLocked: boolean;
    /**
     * Fingerprint of every field above. A change opens a new load wave in the
     * store: the bar restarts and the previous wave's queued fetches are
     * dropped instead of firing stale. Only pages with the bar forward it.
     */
    waveKey: string;
}

export interface PageFilter {
    filter: ResolvedPageFilter;
    /** Takes the range picker's own `onChange` payload: a hand-picked range,
     * which replaces any preset. */
    setPeriod: (dates: RangeValue<Moment>) => void;
    setPreset: (preset: ApiPeriod) => void;
    setRegion: (provinces: string[]) => void;
    setCompare: (enabled: boolean) => void;
}

export interface UsePageFilterOptions {
    search: PageFilterSearch;
    region: PageFilterRegion;
    defaultPeriod?: ApiPeriod;
    campaign?: PageCampaign;
}

/** Narrowed return for `region: 'none'`: `provinces` is provably always empty,
 * so a caller with no region control never needs a local fallback for it. */
export interface PageFilterNoRegion extends Omit<PageFilter, 'filter'> {
    filter: Omit<ResolvedPageFilter, 'provinces'> & { provinces: [] };
}

/**
 * Owns an analytics page's period / region / compare filter. The period is
 * clamped on write AND on read: the picker's `disabledDate` greys cells only
 * for the reader clicking them, and the URL never meets it at all.
 */
export function usePageFilter(
    options: UsePageFilterOptions & { region: 'none' }
): PageFilterNoRegion;
export function usePageFilter(options: UsePageFilterOptions): PageFilter;
export function usePageFilter({
    search,
    region,
    defaultPeriod,
    campaign,
}: UsePageFilterOptions): PageFilter {
    const { params, setParams } = search;

    const { pageRange: range, preset } = resolvePageFilter(
        params,
        defaultPeriod,
        campaign
    );

    const setProvincesParam = useCallback(
        (value: string | undefined) => {
            if (region === 'none') return;
            setParams({ provinces: value });
        },
        [region, setParams]
    );

    const {
        isQcTarget,
        selectedProvinces,
        setSelectedProvinces,
    } = useRegionProvinces({
        appTarget: region === 'none' ? undefined : region.appTarget,
        defaultProvincesCsv:
            region === 'none' ? undefined : region.defaultProvincesCsv,
        queryProvinces:
            region === 'none'
                ? undefined
                : coerceQueryProvinces(params.provinces),
        setProvincesParam,
    });

    // Always called above (rules of hooks), but a `'none'` page's result is
    // discarded here rather than trusted: this is the actual guarantee, not
    // whatever inputs a caller did or didn't pass it.
    const provinces = region === 'none' ? [] : selectedProvinces;

    const compare = resolveCompareEnabled(params.compare);

    const setPeriod = useCallback(
        (dates: RangeValue<Moment>) =>
            handleDateParamsUpdate(dates, ({ startDate, endDate }) => {
                if (!startDate || !endDate) {
                    setParams({
                        period: undefined,
                        startDate: undefined,
                        endDate: undefined,
                    });
                    return;
                }
                const capped = clampToMaxPeriod({
                    start: startDate,
                    end: endDate,
                });
                setParams({
                    period: undefined,
                    startDate: capped.start,
                    endDate: capped.end,
                });
            }),
        [setParams]
    );

    const setPreset = useCallback(
        (next: ApiPeriod) => {
            setParams({
                period: next,
                startDate: undefined,
                endDate: undefined,
            });
        },
        [setParams]
    );

    const setCompare = useCallback(
        (enabled: boolean) =>
            setParams({ compare: enabled ? undefined : false }),
        [setParams]
    );

    const waveKey = [
        range.start,
        range.end,
        preset ?? 'custom',
        [...provinces].sort().join(','),
        compare ? 'compare' : 'no-compare',
    ].join('|');

    const filter = useMemo(
        () => ({
            range,
            preset,
            provinces,
            compare,
            regionLocked: isQcTarget,
            waveKey,
        }),
        // `waveKey` fingerprints every field but `regionLocked`, so the two of
        // them decide identity: `filter` is safe to put in a dependency array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [waveKey, isQcTarget]
    );

    return {
        filter,
        setPeriod,
        setPreset,
        setRegion: setSelectedProvinces,
        setCompare,
    };
}
